import sysend from 'sysend'

interface MessageListener {
  message: string
  handler: (...args: any[]) => any
}

export default () => {
  const { t } = useI18n()
  const { danger } = useToast()
  const { notifyBugsnag } = useReportError()
  const initCallback = createEventHook()
  const errorCallback = createEventHook()

  const subWindow = ref<Window | null>(null)
  const subWindowInitialized = ref(false)
  const isSubWindowOpen = computed(() => !!subWindow.value)
  const messageListeners = ref<{ message: string, handler: () => any }[]>([])

  const cleanup = () => {
    if (subWindow.value)
      subWindow.value.close()

    subWindowInitialized.value = false
    subWindow.value = null
  }

  const { pause, resume: startWindowStatusPoll } = useTimeoutPoll(() => {
    if (!subWindow.value || (subWindow.value && subWindow.value.closed)) {
      cleanup()
      nextTick(() => pause())
    }
  }, 500, { immediate: false })

  const subWindowListener = ({ message, handler }: MessageListener) => {
    const filteredHandler = (...args: any[]) => {
      handler(...args)
    }

    sysend.on(message, filteredHandler)
    messageListeners.value = [...messageListeners.value, { message, handler: filteredHandler }]
  }

  const handleEarlySubWindowUnload = () => {
    if (subWindow.value?.closed) {
      notifyBugsnag(new Error('SubWindow closed unexpectedly'))
      danger(t('unknown-error'))
      cleanup()
    }
  }

  const getCenterPositionForSubWindow = () => {
    const parentWidth = window.outerWidth
    const parentHeight = window.outerHeight
    const parentLeft = window.screenX
    const parentTop = window.screenY
    const width = Math.min(1024, screen.width)
    const height = Math.min(600, screen.height)
    const left = parentLeft + (parentWidth - width) / 2
    const top = parentTop + (parentHeight - height) / 2

    return { top, left, width, height }
  }

  const open = (url: string, title: string) => {
    const { top, left, width, height } = getCenterPositionForSubWindow()

    subWindow.value = window.open(
      url,
      title,
      `popup,
      toolbar=no,
      location=no,
      status=no,
      menubar=no,
      scrollbars=yes,
      resizable=yes,
      width=${width},
      height=${height},
      left=${left},
      top=${top}`,
    )

    if (!(subWindow.value?.opener || subWindow.value?.document) || subWindow.value.closed) {
      errorCallback.trigger()
      cleanup()
      return
    }

    subWindow.value.addEventListener('beforeunload', handleEarlySubWindowUnload)
    startWindowStatusPoll()
  }

  const broadcast = (message: string, data: any) => {
    sysend.broadcast(message, data)
  }

  tryOnMounted(() => {
    subWindowListener({
      message: 'sub-window-manager-initialized',
      handler: () => {
        if (!subWindowInitialized.value) {
          subWindow.value?.removeEventListener('beforeunload', handleEarlySubWindowUnload)
          subWindowInitialized.value = true
        }

        initCallback.trigger()
      },
    })
  })

  tryOnBeforeUnmount(() => {
    messageListeners.value.forEach(({ message, handler }) => sysend.off(message, handler))
  })

  return {
    open,
    broadcast,
    cleanup,
    listen: subWindowListener,
    onInit: initCallback.on,
    onError: errorCallback.on,
    isOpen: isSubWindowOpen,
    initialized: computed(() => subWindowInitialized.value),
  }
}
